import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { getManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.network'
import { ManagerApprovalRulesResponse } from 'src/models/organizations.models'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendMutation,
  UseExtendMutationVariablesBody,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'

interface QueryOrgManagerApprovalRulesOptions
  extends ExtendBaseQueryOptions<ManagerApprovalRulesResponse> {
  orgId: string | undefined
}

export const managerReviewRulesQueries = {
  rules: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.managerApprovalRules(orgId),
      queryFn: () => getManagerReviewRules(),
      enabled: orgId !== undefined,
    }),
}

export const useQueryManagerReviewRules = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrgManagerApprovalRulesOptions) => {
  return useQuery({
    ...managerReviewRulesQueries.rules(orgId),
    // Preserve the factory's arg-presence guard when merging the caller's
    // `enabled` override (spread overrides replace `enabled`, they don't merge).
    enabled: enabled && orgId !== undefined,
    ...rest,
  })
}

export const useManagerReviewRulesEnabled = (): boolean => {
  const { data: user } = useQueryOwnUser({})

  const hasSaasFeatures = useHasSaasFeatures()

  const { data: managerReviewRules } = useQueryManagerReviewRules({
    orgId: user?.organizationId,
    // Conditionally enable this query for only Pro/Enterprise tiers or Saas-excluded orgs,
    // otherwise this will show the SaaS upgrade modal on every render
    enabled: hasSaasFeatures,
  })

  return !!managerReviewRules?.managerReviewEnabled
}

export const usePostManagerReviewRules = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    ManagerApprovalRulesResponse,
    UseExtendMutationVariablesBody<Partial<ManagerApprovalRulesResponse>>
  >(MutationMethod.PATCH, {
    mutationUrl: () => `/expensereview/expensemanagementsettings`,
    onSuccess: (data) => {
      queryClient.setQueryData<ManagerApprovalRulesResponse>(
        orgQueryKeys.managerApprovalRules(data.organizationId),
        data,
      )
    },
  })
}
